import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../styles/inventory.css";
import { ToastContainer, toast } from "react-toastify";
import AssignModal from "../assignmodal";
import { API_BASE_URL } from "../../Config/config";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import {
  useMediaQuery,
  useTheme,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";

const Inventory = ({ setProductCount, setAssets }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [staffName, setStaffName] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffID, setStaffID] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [approvingAuthority, setApprovingAuthority] = useState("");
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [initialCondition, setInitialCondition] = useState("");
  const [iridisID, setIridisID] = useState("");
  const [reasonForAcquisition, setReasonForAcquisition] = useState("");

  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnDate, setReturnDate] = useState("");
  const [returnCondition, setReturnCondition] = useState("");
  const [returnRemarks, setReturnRemarks] = useState("");
  const [assetLocation, setAssetLocation] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [actionType, setActionType] = useState("assign");
  const [reassignLoading, setReassignLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  //fetch Assets
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/assets/inventories`);
      const productsData = Array.isArray(response.data.inventories)
        ? response.data.inventories
        : [];
      setProducts(productsData);
      setProductCount(productsData.length);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch assets.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [setProductCount]);

  // Fetching Data For Staff Assignment
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/staff`);
        const staffData = Array.isArray(response.data.staff)
          ? response.data.staff
          : [];
        setStaffList(staffData);
      } catch (error) {
        setError("Failed to fetch staff list.");
      }
    };
    fetchStaff();
  }, []);

  const handleAssignClick = (product) => {
    setSelectedProduct(product);
    setStaffID("");
    setApprovingAuthority("");
    setExpectedReturnDate("");
    setInitialCondition("");
    setIridisID("");
    setReasonForAcquisition("");
    setActionType("assign");
    setIsModalOpen(true);
  };

  // Handle Submit Assign (First Time Assignment)
  const handleSubmitAssign = async () => {
    try {
      setAssignLoading(true);

      const response = await axios.post(`${API_BASE_URL}/assets/inventories`, {
        staff_id: staffID,
        approving_authority: approvingAuthority,
        expected_return_date: expectedReturnDate,
        initial_condition: initialCondition,
        iridis_id: selectedProduct.iridis_id,
        reason_for_acquisition: reasonForAcquisition,
      });

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p.inventory_id === selectedProduct.inventory_id
            ? { ...p, assignedTo: staffID }
            : p
        )
      );

      setAssets((prevAssets) => [
        ...prevAssets,
        {
          id: selectedProduct.inventory_id,
          name: selectedProduct.asset_name,
          assignee: staffName,
        },
      ]);

      setIsModalOpen(false);

      toast.success("Asset Assigned Successfully.", {
        autoClose: 3000,
        className: "custom-toast-blue",
        onClose: () => {
          fetchProducts();
        },
      });
    } catch (error) {
      setError("Failed to assign the asset.");
      toast.error("Failed to assign the asset. Please try again later.");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleReassignClick = (product) => {
    setSelectedProduct(product);
    setStaffName(product.assignedTo);
    setStaffID("");
    setApprovingAuthority("");
    setExpectedReturnDate("");
    setInitialCondition("");
    setReasonForAcquisition("");
    setActionType("reassign");
    setIsModalOpen(true);
    setIridisID("");
  };

  // Handle Re-Assign (Reassign Existing Asset)
  const handleReassignSubmit = async () => {
    setReassignLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/assets/inventories/reassign`,
        {
          staff_id: staffID,
          approving_authority: approvingAuthority,
          expected_return_date: expectedReturnDate,
          initial_condition: initialCondition,
          reason_for_acquisition: reasonForAcquisition,
          iridis_id: selectedProduct.iridis_id,
        }
      );

      toast.success("Asset Reassigned Successfully.");
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to reassign the asset. Please try again later.");
    } finally {
      setReassignLoading(false);
    }
  };

  // Handle modal submit based on action type
  const handleModalSubmit = () => {
    if (actionType === "assign") {
      handleSubmitAssign();
    } else if (actionType === "reassign") {
      handleReassignSubmit();
    }
  };

  // Handle Return when Return Button is clicked
  const handleReturnClick = (inventory_id, staff_id) => {
    const product = products.find((p) => p.inventory_id === inventory_id);
    setSelectedProduct(product);
    setIsReturnModalOpen(true);
    setReturnDate("");
    setReturnCondition("");
    setReturnRemarks("");
  };

  const handleSubmitReturn = (inventory_id, staff_id) => {
    if (!returnCondition || !assetLocation) {
      toast.error("Please fill in all return details before submitting.", {
        autoClose: 5000,
        className: "custom-toast-error",
      });
      return;
    }

    setReturnLoading(true);
    handleReturnProcess(inventory_id, staff_id);
  };

  //Post return values to backend
  const handleReturnProcess = async (staffID) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/assets/inventories/return`,
        {
          return_date: returnDate,
          condition: returnCondition,
          return_remarks: null,
          staff_id: staffID,
          location: assetLocation,
          approving_authority: null,
          expected_return_date: null,
          initial_condition: null,
          iridis_id: selectedProduct.iridis_id,
          reason_for_acquisition: null,
        }
      );
      toast.success("The asset has been successfully returned.", {
        autoClose: 3000,
        className: "custom-toast-green",
        onClose: fetchProducts,
      });
      setReturnDate("");
      setReturnCondition("");
      setReturnRemarks("");
      setIsReturnModalOpen(false);
    } catch (error) {
      setError("Failed to return the asset. Please try again later.");
      toast.error("Failed to return the asset. Please try again later.");
    } finally {
      fetchProducts();
    }
  };

  // Handle Download
  const handleDownloadAll = () => {
    const csvRows = [];

    csvRows.push([
      "Iridis ID Number",
      "Asset Name",
      "Assigned To",
      "Location",
      "Status",
      "Assignment Status",
    ]);

    products.forEach((product) => {
      const status = product.assignedTo ? "Assigned" : "Not Assigned";
      const assignmentStatus = product.assignment_status || "Not Available";

      csvRows.push([
        product.iridis_id,
        product.asset_name,
        product.assignedTo || "Not Assigned",
        product.asset_location || "N/A",
        status,
        assignmentStatus,
      ]);
    });

    const csvString = csvRows.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", "inventory_with_assignment_status.csv");
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredProducts = products.filter((product) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      product.iridis_id.toString().toLowerCase().includes(searchTermLower) ||
      product.asset_name.toLowerCase().includes(searchTermLower)
    );
  });

  // Calculate pagination
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const getVisiblePages = () => {
    const delta = isMobile ? 1 : 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }
    }

    return rangeWithDots;
  };

  // Pagination Component
  const PaginationControls = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "20px",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "15px" : "0",
      }}
    >
      {/* Items per page selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Items per page</InputLabel>
          <Select
            value={itemsPerPage}
            label="Items per page"
            onChange={handleItemsPerPageChange}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Showing {Math.min(startIndex + 1, totalItems)} -{" "}
          {Math.min(endIndex, totalItems)} of {totalItems} items
        </Typography>
      </div>

      {/* Pagination buttons */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            size="small"
          >
            <FirstPageIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>

          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === "number" && handlePageChange(page)}
              disabled={typeof page !== "number"}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                backgroundColor:
                  page === currentPage ? "#007bff" : "transparent",
                color: page === currentPage ? "white" : "#333",
                cursor: typeof page === "number" ? "pointer" : "default",
                borderRadius: "4px",
                fontSize: "14px",
                minWidth: "40px",
              }}
            >
              {page}
            </button>
          ))}

          <IconButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            size="small"
          >
            <LastPageIcon />
          </IconButton>
        </div>
      )}
    </div>
  );

  //  loading Screen
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p
          className="loading-text"
          style={{ color: "black", fontSize: "18px", marginTop: "10px" }}
        >
          Loading Assets...
        </p>
      </div>
    );
  }
  if (error) return <p style={{ marginTop: "30px" }}>{error}</p>;

  const ProductCard = ({ product }) => (
    <div className="product-card" key={product.iridis_id}>
      <div className="product-card-header">
        <h3 className="product-id">ID: {product.iridis_id}</h3>
      </div>
      <div className="product-card-body">
        <p className="product-name">{product.asset_name}</p>
        <div className="product-actions">
          {product.assignment_status === "assigned" ? (
            <div className="action-buttons">
              <button
                className="reassign-button"
                onClick={() => handleReassignClick(product)}
              >
                Reassign
              </button>
              <button
                className="return-button"
                onClick={() =>
                  handleReturnClick(product.inventory_id, product.staff_id)
                }
              >
                Return
              </button>
            </div>
          ) : (
            <button
              className="assign-button"
              onClick={() => handleAssignClick(product)}
            >
              Assign
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const ProductRow = ({ product }) => (
    <tr key={product.iridis_id}>
      <td data-label="Iridis ID">{product.iridis_id}</td>
      <td data-label="Asset Name">{product.asset_name}</td>
      <td data-label="Action">
        {product.assignment_status === "assigned" ? (
          <div className="action-buttons">
            <button
              className="reassign-button"
              onClick={() => handleReassignClick(product)}
            >
              Reassign
            </button>
            <button
              className="return-button"
              onClick={() =>
                handleReturnClick(product.inventory_id, product.staff_id)
              }
            >
              Return
            </button>
          </div>
        ) : (
          <button
            className="assign-button"
            onClick={() => handleAssignClick(product)}
          >
            Assign
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div
      className="inventory-container"
      style={{
        padding: isMobile ? "10px" : "20px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <h1
        className="inventory-title"
        style={{
          fontSize: isMobile ? "24px" : "30px",
          color: "#000000",
          marginBottom: isMobile ? "15px" : "20px",
          textAlign: isMobile ? "center" : "left",
        }}
      >
        Inventory
      </h1>
      <ToastContainer />

      <div
        className="control-container"
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "10px" : "15px",
          marginBottom: "20px",
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <div
          className="search-container"
          style={{ flex: isMobile ? "none" : "1" }}
        >
          <input
            type="text"
            className="search-input"
            placeholder="Search for assets..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: "100%",
              padding: isMobile ? "12px" : "10px",
              fontSize: isMobile ? "16px" : "14px",
              minWidth: 0,
            }}
          />
        </div>

        <div className="button-container">
          <button
            className="download-button"
            onClick={handleDownloadAll}
            style={{
              padding: isMobile ? "12px 16px" : "10px 16px",
              fontSize: isMobile ? "14px" : "13px",
              whiteSpace: "nowrap",
            }}
          >
            {isMobile ? "Download" : "Download All"}
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div className="mobile-container">
          {currentProducts.length === 0 ? (
            <div className="no-assets">
              <p>Assets in inventory will be displayed here.</p>
            </div>
          ) : (
            <div className="products-grid">
              {currentProducts.map((product) => (
                <ProductCard key={product.iridis_id} product={product} />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="table-container" style={{ overflowX: "auto" }}>
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Iridis ID Number</th>
                <th>Asset Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.length === 0 ? (
                <tr>
                  <td colSpan="3">
                    Assets in inventory will be displayed here.
                  </td>
                </tr>
              ) : (
                currentProducts.map((product) => (
                  <ProductRow key={product.iridis_id} product={product} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Enhanced Pagination Controls */}
      {filteredProducts.length > 0 && <PaginationControls />}

      {/* Assignment/Reassignment Modal */}
      <AssignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProduct={selectedProduct}
        actionType={actionType}
        staffID={staffID}
        setStaffID={setStaffID}
        staffList={staffList}
        approvingAuthority={approvingAuthority}
        setApprovingAuthority={setApprovingAuthority}
        expectedReturnDate={expectedReturnDate}
        setExpectedReturnDate={setExpectedReturnDate}
        initialCondition={initialCondition}
        setInitialCondition={setInitialCondition}
        reasonForAcquisition={reasonForAcquisition}
        setReasonForAcquisition={setReasonForAcquisition}
        onSubmit={handleModalSubmit}
        loading={assignLoading || reassignLoading}
        error={error}
      />

      {/* Return Modal */}
      {isReturnModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close"
              onClick={() => setIsReturnModalOpen(false)}
            >
              ×
            </button>
            <h2>Return Asset</h2>

            <p className="returning-message">
              Returning asset with Iridis ID: {selectedProduct.iridis_id}
            </p>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {returnLoading ? (
              <div className="assign-screen">
                <p>Returning Asset...</p>
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="modal-form">
                <label>
                  Condition on Return:
                  <select
                    name="returnCondition"
                    value={returnCondition}
                    onChange={(e) => setReturnCondition(e.target.value)}
                    required
                  >
                    <option value="---">Select Condition</option>
                    <option value="excellent">Excellent</option>
                    <option value="used">Used</option>
                    <option value="poor">Poor</option>
                    <option value="damaged">Damaged</option>
                    <option value="needs-repair">Needs Repair</option>
                  </select>
                </label>

                <label>
                  Location:
                  <select
                    name="assetLocation"
                    value={assetLocation}
                    onChange={(e) => setAssetLocation(e.target.value)}
                    required
                  >
                    <option value="">Select Location</option>
                    <option value="Business Office">Business Office</option>
                    <option value="Conference Office">Conference Office</option>
                    <option value="Lobby">Lobby</option>
                    <option value="Waiting Area">Waiting Area</option>
                    <option value="Tech Lab">Tech Lab</option>
                    <option value="Developers' Office">
                      Developers' Office
                    </option>
                    <option value="Executive Office">Executive Office</option>
                    <option value="Staff Conference Room">
                      Staff Conference Room
                    </option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Gravel Garden">Gravel Garden</option>
                    <option value="Security Post">Security Post</option>
                  </select>
                </label>

                <button
                  className="modal-submit"
                  onClick={() => handleSubmitReturn(selectedProduct.staff_id)}
                >
                  Confirm Return
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
